'use client';

/**
 * Enhanced i18n Provider with Backend Integration
 * Future-proof design for easy switching between static files and database translations
 */

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { I18N_LANGUAGES, I18N_CONFIG } from '@/i18n/config';
import { DirectionProvider as RadixDirectionProvider } from '@radix-ui/react-direction';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { I18nextProvider, initReactI18next, setDefaults } from 'react-i18next';
import { BackendAuth } from '@/lib/auth-backend';

// Static fallback translations (HMR cache-bust 2026-07-14T02:45)
import arTranslations from '@/i18n/messages/ar.json';
import chTranslations from '@/i18n/messages/ch.json';
import deTranslations from '@/i18n/messages/de.json';
import enTranslations from '@/i18n/messages/en.json';
import esTranslations from '@/i18n/messages/es.json';

// CRITICAL (SSR hang fix): react-i18next's useTranslation() SUSPENDS (throws a
// promise) when the i18n instance isn't initialized yet. Our i18n.init() runs in
// a useEffect (client-only), so during the SERVER render the instance is still
// uninitialized and react-i18next falls back to its default useSuspense:true.
// Any page that calls useTranslation() in its render tree then suspends the root
// layout's <Suspense> boundary — with no server-side resolver the RSC stream is
// held open until the client disconnects (browser tab "loads forever"): this was
// the cause of the auth pages /signin, /reset-password, /change-password, /signup
// and /verify-email never finishing their SSR document (dev AND prod).
// Setting the react-i18next default to useSuspense:false at MODULE LOAD time
// (runs on server AND client before any render) makes the hook return synchronously
// during SSR instead of suspending. The per-init react.useSuspense:false in
// initializeBasicI18n()/init() is kept for the initialized (client) instance.
setDefaults({ useSuspense: false });

// Belt-and-suspenders: the useTranslation() hook reads useSuspense from
// i18n.options.react. Before init() has run (i.e. during SSR) that object is
// empty, so seed it synchronously at module load so the hook never suspends.
if (!i18n.options) i18n.options = {};
i18n.options.react = { ...(i18n.options.react || {}), useSuspense: false };

/**
 * Helper: Flatten nested translation bundle to get all keys
 * Used for comparing translations completeness between languages
 */
function getAllKeysFromBundle(bundle, prefix = '') {
  const keys = [];
  if (!bundle || typeof bundle !== 'object') return keys;

  for (const [key, value] of Object.entries(bundle)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      keys.push(...getAllKeysFromBundle(value, fullKey));
    } else {
      keys.push(fullKey);
    }
  }
  return keys;
}

// Hard timeout (ms) for any BLOCKING translation fetch. Translation loading must
// NEVER block app rendering: if the backend is slow or mid-restart (uvicorn
// --reload), we abort fast and fall back to the bundled static translations
// instead of leaving the whole app frozen on the i18n "Loading…" gate.
const TRANSLATION_FETCH_TIMEOUT_MS = 2500;

/**
 * fetch() with a hard timeout via AbortController. On timeout the promise
 * rejects with an AbortError, which existing callers already catch and handle
 * by falling back to static translations.
 */
async function fetchWithTimeout(url, options = {}, timeoutMs = TRANSLATION_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// Enhanced i18n Context
const EnhancedI18nContext = createContext();

// Translation loading strategies
const TRANSLATION_STRATEGY = {
  STATIC_ONLY: 'static',
  BACKEND_WITH_FALLBACK: 'backend_fallback',
  BACKEND_ONLY: 'backend'
};

// Current strategy - easily switchable
const CURRENT_STRATEGY = TRANSLATION_STRATEGY.BACKEND_WITH_FALLBACK;

/**
 * LRU (Least Recently Used) Cache with size limit
 * Prevents unbounded memory growth by evicting oldest entries
 */
class LRUCache {
  constructor(maxSize = 50) {
    this.maxSize = maxSize;
    this.cache = new Map();
  }

  get(key) {
    if (!this.cache.has(key)) return undefined;

    // Move to end (most recently used)
    const value = this.cache.get(key);
    this.cache.delete(key);
    this.cache.set(key, value);

    return value;
  }

  set(key, value) {
    // Remove if exists (will re-add at end)
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    this.cache.set(key, value);

    // Evict oldest entry if size exceeded
    if (this.cache.size > this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);

    }
  }

  has(key) {
    return this.cache.has(key);
  }

  get size() {
    return this.cache.size;
  }

  clear() {
    this.cache.clear();
  }
}

class TranslationLoader {
  constructor() {
    // Use LRU cache with max 50 entries instead of unbounded Map
    this.cache = new LRUCache(50);
    this.missingKeys = new Set();
    this.loadingPromises = new Map();

    // Periodic cleanup of missingKeys to prevent memory leak
    this.missingKeysCleanupInterval = setInterval(() => {
      if (this.missingKeys.size > 1000) {
        this.missingKeys.clear();
      }
    }, 5 * 60 * 1000); // Every 5 minutes

    // localStorage cache key prefix
    this.STORAGE_PREFIX = 'xoffi_translations_';
    this.STORAGE_VERSION = 'v20_'; // Increment to invalidate old caches (v20: 2026-07-07 fixed 11 clobbered EN values in nexus_core.* — delete_confirm/focus_mode/group_badges had raw key-suffix as EN text)
  }

  destroy() {
    // Cleanup interval on unmount
    if (this.missingKeysCleanupInterval) {
      clearInterval(this.missingKeysCleanupInterval);
    }
  }

  /**
   * Load translations with strategy-based approach
   */
  async loadTranslations(languageCode, strategy = CURRENT_STRATEGY) {
    const cacheKey = `${languageCode}_${strategy}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Prevent duplicate requests
    if (this.loadingPromises.has(cacheKey)) {
      return await this.loadingPromises.get(cacheKey);
    }

    const loadPromise = this._loadTranslationsInternal(languageCode, strategy);
    this.loadingPromises.set(cacheKey, loadPromise);

    try {
      const result = await loadPromise;
      this.cache.set(cacheKey, result);
      return result;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  async _loadTranslationsInternal(languageCode, strategy) {
    switch (strategy) {
      case TRANSLATION_STRATEGY.STATIC_ONLY:
        return this._loadStaticTranslations(languageCode);
      
      case TRANSLATION_STRATEGY.BACKEND_ONLY:
        return this._loadBackendTranslations(languageCode);
      
      case TRANSLATION_STRATEGY.BACKEND_WITH_FALLBACK:
      default:
        return this._loadWithFallback(languageCode);
    }
  }

  /**
   * Load static translations (current system)
   */
  _loadStaticTranslations(languageCode) {
    const staticTranslations = {
      en: enTranslations,
      ar: arTranslations,
      es: esTranslations,
      de: deTranslations,
      ch: chTranslations,
    };

    return staticTranslations[languageCode] || {};
  }

  /**
   * Get translations from localStorage cache
   */
  _getFromLocalStorage(languageCode) {
    if (typeof window === 'undefined') return null;

    try {
      const key = `${this.STORAGE_PREFIX}${this.STORAGE_VERSION}${languageCode}`;
      const cached = localStorage.getItem(key);

      if (cached) {
        const parsed = JSON.parse(cached);
        const age = Date.now() - parsed.timestamp;

        // Cache valid for 1 hour AND must have enough translations (> 50 keys)
        // This prevents returning corrupted/partial caches
        const translationCount = parsed.translations ? Object.keys(parsed.translations).length : 0;
        if (age < 60 * 60 * 1000 && translationCount > 50) {
          return parsed.translations;
        } else {
          // Cache is stale OR has too few translations - remove it
          localStorage.removeItem(key);
          console.log(`[TranslationLoader] Cleared invalid cache for ${languageCode} (age: ${Math.round(age/1000)}s, keys: ${translationCount})`);
        }
      }
    } catch (error) {
      console.error('Failed to read from localStorage:', error);
    }

    return null;
  }

  /**
   * Save translations to localStorage cache
   */
  _saveToLocalStorage(languageCode, translations) {
    if (typeof window === 'undefined') return;

    try {
      const key = `${this.STORAGE_PREFIX}${this.STORAGE_VERSION}${languageCode}`;
      const data = {
        translations,
        timestamp: Date.now()
      };

      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
      // Quota exceeded - clear old caches
      this._clearOldCaches();
    }
  }

  /**
   * Clear old translation caches from localStorage
   */
  _clearOldCaches() {
    if (typeof window === 'undefined') return;

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_PREFIX) && !key.startsWith(`${this.STORAGE_PREFIX}${this.STORAGE_VERSION}`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Failed to clear old caches:', error);
    }
  }

  /**
   * Load translations from backend API (with localStorage cache)
   */
  async _loadBackendTranslations(languageCode) {
    // Try localStorage cache first
    const cached = this._getFromLocalStorage(languageCode);
    if (cached) {
      return cached;
    }

    try {
      // ALWAYS use organization_id = 1 for translations (global standard)
      const organizationId = 1; // NEVER use user organisation for translations!

      // Use new endpoint that loads ALL translations for a language.
      // Hard timeout so a slow/restarting backend never blocks the i18n gate.
      const response = await fetchWithTimeout(
        `/api/translations/language/${languageCode}?namespace=translation&organization_id=${organizationId}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        },
        TRANSLATION_FETCH_TIMEOUT_MS
      );

      if (!response.ok) {
        throw new Error(`Backend translation request failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Save to localStorage cache
        this._saveToLocalStorage(languageCode, data.translations);

        // The API already returns nested structure, return as-is
        return data.translations;
      } else {
        throw new Error('Backend returned error');
      }
    } catch (error) {
      console.error(`❌ [TranslationLoader] Backend translation loading failed for ${languageCode}:`, error);
      console.error(`❌ [TranslationLoader] Error name:`, error.name);
      console.error(`❌ [TranslationLoader] Error message:`, error.message);
      console.error(`❌ [TranslationLoader] Error stack:`, error.stack);
      return {};
    }
  }

  /**
   * Load with backend first, merge with static fallback
   */
  async _loadWithFallback(languageCode) {
    // Load static translations as base
    const staticTranslations = this._loadStaticTranslations(languageCode);

    try {
      // Load backend translations
      const backendTranslations = await this._loadBackendTranslations(languageCode);

      // Deep merge backend with static (backend takes priority)
      if (Object.keys(backendTranslations).length > 0) {
        return this._deepMerge(staticTranslations, backendTranslations);
      }
    } catch (error) {
      // Backend translation failed, using static only
    }

    return staticTranslations;
  }

  /**
   * Deep merge two objects (source overwrites target)
   */
  _deepMerge(target, source) {
    const output = Object.assign({}, target);
    if (this._isObject(target) && this._isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this._isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this._deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  }

  _isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  /**
   * Flatten nested translation object for i18next
   */
  _flattenTranslations(translations) {
    const flattened = {};
    
    for (const [key, value] of Object.entries(translations)) {
      if (typeof value === 'object' && value !== null) {
        // Handle nested objects
        const nested = this._flattenTranslations(value);
        for (const [nestedKey, nestedValue] of Object.entries(nested)) {
          flattened[`${key}.${nestedKey}`] = nestedValue;
        }
      } else {
        flattened[key] = value;
      }
    }
    
    return flattened;
  }

  /**
   * Report missing translation key to backend
   */
  async reportMissingKey(key, language, context = null) {
    // Avoid duplicate reports
    const reportKey = `${key}_${language}`;
    if (this.missingKeys.has(reportKey)) {
      return;
    }
    
    this.missingKeys.add(reportKey);
    
    try {
      // Don't wait for response - fire and forget
      fetch('/api/translations/missing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key,
          language,
          context: context || window.location.pathname,
          user_agent: navigator.userAgent
        })
      }).catch(error => {
        // Failed to report missing translation
      });
    } catch (error) {
      // Failed to report missing translation
    }
  }

  /**
   * Get single translation from backend
   */
  async getTranslation(key, language, namespace = 'default') {
    // 🛑 BLOCK: Skip if key was batch-loaded (prevents redundant individual requests!)
    if (typeof window !== 'undefined' && window.__batchLoadedTranslationKeys?.has(key)) {
      return null;
    }

    try {
      const response = await fetch(`/api/translations/key/${encodeURIComponent(key)}?language=${language}&namespace=${namespace}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return data.value;
        }
      }
    } catch (error) {
      // Failed to get translation
    }

    return null;
  }
}

// Global translation loader instance
const translationLoader = new TranslationLoader();

/**
 * Normalize language code to base language (en-US -> en, de-DE -> de, etc.)
 * This ensures we load translations from DB which uses base language codes
 */
// Full set of supported base language codes, derived from the canonical
// I18N_LANGUAGES config (NOT a hardcoded subset). Region variants like
// `de-AT`/`en-US` normalize down to their base (`de`/`en`); any base that is a
// configured language (tr, pt, nl, pl, ru, …) is preserved instead of being
// silently forced to German.
const NORMALIZE_SUPPORTED_CODES = new Set(
  I18N_LANGUAGES.map((lang) => lang.code)
);

function normalizeLanguageCode(langCode) {
  if (!langCode) return 'de';
  // Extract base language (first part before hyphen or underscore)
  const base = langCode.split(/[-_]/)[0].toLowerCase();
  // Preserve any configured language; fall back to German only for truly unknown codes.
  return NORMALIZE_SUPPORTED_CODES.has(base) ? base : 'de';
}

// 🔥 Module-level Promise for provider ready state
// This allows useBatchTranslation to await provider initialization
let providerReadyResolve = null;
let providerReadyPromise = new Promise(resolve => {
  providerReadyResolve = resolve;
});

// 🔥 NEW: Flag to track if bundle is ACTUALLY populated (not just state updated)
let bundleActuallyPopulated = false;

// Export function to wait for provider
export const waitForProviderReady = () => providerReadyPromise;

// 🔥 NEW: Export function to check if bundle is populated
export const isBundlePopulated = () => bundleActuallyPopulated;

function EnhancedI18nProvider({ children }) {

  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false); // NEW: Blocks rendering until initial translations load
  const [translationStrategy, setTranslationStrategy] = useState(CURRENT_STRATEGY);
  const [supportedLanguages, setSupportedLanguages] = useState(I18N_LANGUAGES);
  const [translationsVersion, setTranslationsVersion] = useState(0); // Counter to force re-renders

  // 🔥 CRITICAL: Expose increment function for useBatchTranslation to trigger re-renders
  const incrementTranslationsVersion = useCallback(() => {
    setTranslationsVersion(prev => prev + 1);
  }, []);

  // 🔥 Reset the provider ready promise on mount (for HMR)
  useEffect(() => {
    if (!isInitialLoadComplete) {
      bundleActuallyPopulated = false; // Reset flag
      providerReadyPromise = new Promise(resolve => {
        providerReadyResolve = resolve;
      });
    }
  }, []);

  useEffect(() => {
    // Init erst NACH abgeschlossener Hydration starten (Idle-Callback):
    // i18n.init mit den statischen Bundles ist so schnell, dass es sonst
    // fertig wäre, bevor der Suspense-Inhalt der Seite hydratisiert hat —
    // die Kinder würden gegen übersetzte Texte hydratisieren, während das
    // Server-HTML noch rohe Keys enthält (Hydration-Mismatch). Idle = die
    // Hydration-Arbeit auf dem Main-Thread ist durch.
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => initializeI18n(), { timeout: 300 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timerId = setTimeout(() => initializeI18n(), 50);
    return () => clearTimeout(timerId);
  }, []);

  // NOTE: Delayed force-load removed - main initialization now properly waits for translations
  // The language change handler will reload resources when needed

  const initializeI18n = async () => {
    if (i18n.isInitialized) {
      // Even if initialized, we need to load translations!
      // 🔥 FIX: Normalize browser locale (en-US -> en) to match database
      const detectedLanguage = i18n.language || I18N_CONFIG.defaultLanguage;
      const currentLanguage = normalizeLanguageCode(detectedLanguage);

      // Switch i18next to normalized language if needed
      if (detectedLanguage !== currentLanguage) {
        await i18n.changeLanguage(currentLanguage);
      }

      // Non-blocking: Instanz ist initialisiert (statische Bundles vorhanden),
      // also sofort entblocken — Backend-Übersetzungen mergen im Hintergrund
      // nach und bumpen translationsVersion (Re-Render mit Org-Overrides).
      bundleActuallyPopulated = true;
      setIsI18nInitialized(true);
      setIsInitialLoadComplete(true);
      if (providerReadyResolve) providerReadyResolve();

      loadLanguageResources(currentLanguage)
        .then(() => {
          // Force all useTranslation hooks to re-render with loaded translations
          i18n.emit('languageChanged', i18n.language);
        })
        .catch(() => {});
      return;
    }

    try {
      // Sprachliste non-blocking laden (vorher blockierte das die komplette
      // App bis zu 2,5s vor dem i18n.init)
      loadSupportedLanguages();

      // Mit den STATISCHEN Bundles initialisieren (liegen bereits im JS-Bundle):
      // die App ist damit sofort renderbar; Backend-Übersetzungen (Org-Overrides,
      // neue Keys) werden unten non-blocking darübergemergt.
      await i18n
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
          resources: {
            en: { translation: enTranslations },
            ar: { translation: arTranslations },
            es: { translation: esTranslations },
            de: { translation: deTranslations },
            ch: { translation: chTranslations },
          },
          fallbackLng: [I18N_CONFIG.fallbackLanguage, I18N_CONFIG.ultimateFallback], // German -> English fallback
          debug: false,
          saveMissing: true, // CRITICAL: Triggers missingKey event even when fallback exists

          interpolation: {
            escapeValue: false,
          },

          detection: {
            order: ['localStorage', 'navigator', 'htmlTag'],
            caches: ['localStorage'],
            lookupLocalStorage: 'language',
          },

          react: {
            useSuspense: false,
          },

          // Custom missing key handler - DISABLED individual fetches
          // Use useBatchTranslation() for loading translations instead
          missingKeyHandler: (lngs, ns, key, fallbackValue) => {
            // NOTE: Event dispatching and individual fetches REMOVED!
            // This prevents 200+ redundant API requests.
            return fallbackValue || key;
          },

          // Dynamic resource loading
          backend: {
            loadPath: async (lng, ns) => {
              const translations = await translationLoader.loadTranslations(lng, translationStrategy);
              return translations;
            }
          }
        });

      // 🔥 FIX: Normalize browser locale (en-US -> en) to match database
      const detectedLanguage = i18n.language || I18N_CONFIG.defaultLanguage;
      const initialLanguage = normalizeLanguageCode(detectedLanguage);

      // Switch i18next to normalized language if needed
      if (detectedLanguage !== initialLanguage) {
        await i18n.changeLanguage(initialLanguage);
      }

      // Statische Bundles sind geladen → sofort entblocken. Kein Warten auf
      // Backend-Übersetzungen mehr (das blockierte die ganze App hinter einem
      // Vollbild-Spinner und remountete alle Provider darunter).
      bundleActuallyPopulated = true;
      setIsI18nInitialized(true);
      setIsInitialLoadComplete(true);
      if (providerReadyResolve) providerReadyResolve();

      // Backend-Übersetzungen non-blocking und PARALLEL nachladen: aktive
      // Sprache + deutscher Fallback gleichzeitig (vorher sequenziell — der
      // de-Load wartete auf den kompletten en-Download). Der Fallback-Preload
      // bleibt nötig, damit unübersetzte Keys lesbar deutsch degradieren
      // (i18next fallbackLng greift nur auf geladene Ressourcen).
      const backgroundLoads = [loadLanguageResources(initialLanguage)];
      if (initialLanguage !== I18N_CONFIG.fallbackLanguage) {
        backgroundLoads.push(loadLanguageResources(I18N_CONFIG.fallbackLanguage));
      }
      Promise.all(backgroundLoads)
        .then(() => {
          // Force all useTranslation hooks to re-render with loaded translations
          i18n.emit('languageChanged', i18n.language);
        })
        .catch(() => {});

    } catch (error) {
      console.error('❌ [I18nProvider] i18n initialization failed:', error);
      // Fallback to basic initialization
      await initializeBasicI18n();
      bundleActuallyPopulated = true; // Set flag even on error fallback
      setIsI18nInitialized(true);
      setIsInitialLoadComplete(true); // Allow rendering even on error
      // 🔥 Resolve the provider ready promise even on error
      if (providerReadyResolve) providerReadyResolve();
      // 🔥 FIX: Force all useTranslation hooks to re-render with loaded translations
      i18n.emit('languageChanged', i18n.language);
    }
  };

  const initializeBasicI18n = async () => {
    const resources = {
      en: { translation: enTranslations },
      ar: { translation: arTranslations },
      es: { translation: esTranslations },
      de: { translation: deTranslations },
      ch: { translation: chTranslations },
    };

    await i18n
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        resources,
        fallbackLng: 'de',
        debug: false,
        interpolation: { escapeValue: false },
        detection: {
          order: ['localStorage', 'navigator', 'htmlTag'],
          caches: ['localStorage'],
          lookupLocalStorage: 'language',
        },
        react: { useSuspense: false },
      });
  };

  const loadSupportedLanguages = async () => {
    try {
      const response = await fetchWithTimeout('/api/translations/languages', {}, TRANSLATION_FETCH_TIMEOUT_MS);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSupportedLanguages(data.languages);
          return;
        }
      }
    } catch (error) {
      // Failed to load supported languages from backend
    }

    // Fallback to static config

  };

  const loadLanguageResources = useCallback(async (languageCode) => {
    try {
      // 🔥 FIX: Normalize language code (en-US -> en) to match database
      const normalizedCode = normalizeLanguageCode(languageCode);

      // Load translations using normalized code (what's in the database)
      const translations = await translationLoader.loadTranslations(normalizedCode, translationStrategy);

      // Add resources to i18next using normalized code
      i18n.addResourceBundle(normalizedCode, 'translation', translations, true, true);

      // 🔥 CRITICAL: Increment version counter to force re-render of all components
      setTranslationsVersion(prev => prev + 1);

      // Return loaded translations for completeness check
      return translations;
    } catch (error) {
      console.error(`❌ [I18nProvider] Failed to load resources for ${languageCode}:`, error);
      return {}; // Return empty object on error
    }
  }, [translationStrategy]); // Dependencies: recreate when strategy changes

  // Handle language changes
  useEffect(() => {
    const handleLanguageChange = async (lng) => {
      // Update document direction
      const language = supportedLanguages.find(lang => lang.code === lng);
      if (language?.direction) {
        document.documentElement.setAttribute('dir', language.direction);
      }

      // ALWAYS load resources - don't trust hasResourceBundle due to HMR caching
      await loadLanguageResources(lng);
    };

    // Set initial direction
    if (i18n.language) {
      handleLanguageChange(i18n.language);
    }

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [supportedLanguages]);

  // Context value - useMemo to ensure translationsVersion changes trigger re-renders
  const contextValue = useMemo(() => ({
    translationStrategy,
    setTranslationStrategy,
    supportedLanguages,
    loadLanguageResources,
    translationLoader,
    isBackendAvailable: translationStrategy !== TRANSLATION_STRATEGY.STATIC_ONLY,
    translationsVersion, // Expose version counter to trigger re-renders
    incrementTranslationsVersion, // 🔥 Allow useBatchTranslation to trigger re-renders
    isProviderReady: isInitialLoadComplete // 🔥 NEW: Signals when initial translations are ready
  }), [translationStrategy, supportedLanguages, loadLanguageResources, translationsVersion, incrementTranslationsVersion, isInitialLoadComplete]); // Re-create when these change

  // Get current language for direction
  const currentLanguage = supportedLanguages.find(
    lang => lang.code === (i18n.language || 'de')
  ) || supportedLanguages[0] || { direction: 'ltr' };

  // Bewusst KEIN Blocking-Gate mehr: i18n wird mit den statischen Bundles
  // initialisiert und ist sofort renderbar; Backend-Übersetzungen mergen im
  // Hintergrund nach (translationsVersion-Bump re-rendert die Konsumenten).
  // Der frühere Vollbild-Spinner unmountete/remountete zudem alle Provider
  // darunter und löste deren Mount-Fetches doppelt aus.
  return (
    <EnhancedI18nContext.Provider value={contextValue}>
      <I18nextProvider i18n={i18n}>
        <RadixDirectionProvider dir={currentLanguage.direction}>
          {children}
        </RadixDirectionProvider>
      </I18nextProvider>
    </EnhancedI18nContext.Provider>
  );
}

// Enhanced useLanguage hook
const useEnhancedLanguage = () => {
  const context = useContext(EnhancedI18nContext);
  // 🔥 FIX: Normalize language code on init (en-US -> en) to match I18N_LANGUAGES
  const [currentLangCode, setCurrentLangCode] = useState(() =>
    normalizeLanguageCode(i18n.language || 'de')
  );
  const [, forceUpdate] = useState(0); // Force update when translations load

  // Track i18n language changes
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      // Normalize incoming language code
      setCurrentLangCode(normalizeLanguageCode(lng));
    };

    i18n.on('languageChanged', handleLanguageChanged);

    // Set initial value (normalized)
    if (i18n.language) {
      setCurrentLangCode(normalizeLanguageCode(i18n.language));
    }

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  // 🔥 CRITICAL: Force re-render when translations finish loading
  useEffect(() => {
    if (context?.translationsVersion !== undefined) {
      forceUpdate(prev => prev + 1);
    }
  }, [context?.translationsVersion]);

  // Always use I18N_LANGUAGES to get the full language object with flag
  const currentLanguage = I18N_LANGUAGES.find(
    lang => lang.code === currentLangCode
  ) || I18N_LANGUAGES[0];

  const changeLanguage = useCallback(async (code) => {
    // Preload resources before switching and capture what was loaded
    let loadedTranslations = {};
    if (context?.loadLanguageResources) {
      loadedTranslations = await context.loadLanguageResources(code);
    }

    // Client-Side Translation Completeness Check
    // Compare German (reference) keys vs target language keys FROM BACKEND RESPONSE
    let missingKeys = [];
    if (code !== 'de') {
      try {
        const germanBundle = i18n.getResourceBundle('de', 'translation');

        const germanKeys = new Set(getAllKeysFromBundle(germanBundle));
        // Use actually loaded translations from backend, not i18next bundle (which may be empty)
        const targetKeys = new Set(getAllKeysFromBundle(loadedTranslations));

        // Find missing keys in target language
        missingKeys = [...germanKeys].filter(k => !targetKeys.has(k));
      } catch (error) {
        // Translation completeness check failed
      }
    }

    // Switch language FIRST
    await i18n.changeLanguage(code);
    setCurrentLangCode(code);  // Force update

    // Persist to localStorage explicitly (i18next detector also does this, but be explicit)
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', code);
    }

    // Immediately force-rerender all useTranslation consumers.
    // loadLanguageResources() already incremented translationsVersion once (with old i18n.language),
    // so we need a second tick now that i18n.language === code so t() calls return new strings.
    if (context?.incrementTranslationsVersion) {
      context.incrementTranslationsVersion();
    }

    // Broadcast new language to all embedded module iframes
    if (typeof document !== 'undefined') {
      document.querySelectorAll('iframe').forEach((iframe) => {
        try {
          iframe.contentWindow?.postMessage({ type: 'xoffi:lang', lang: code }, '*');
        } catch (_e) {
          // cross-origin iframe blocked — ignore
        }
      });
    }

    // Trigger auto-translation AFTER language switch and re-render
    // This ensures components are mounted with new language and event listeners are ready
    if (missingKeys.length > 0) {
      setTimeout(() => {
        missingKeys.forEach(key => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('translationMissing', {
              detail: {
                key,
                language: code,
                namespace: 'translation'
              }
            }));
          }
        });
      }, 200); // Delay to ensure components have re-rendered with new language
    }
  }, [context]);

  const translateDynamic = useCallback(async (text, targetLang = null, context = null) => {
    if (!context?.translationLoader) return text;
    
    const lang = targetLang || i18n.language;
    
    try {
      const response = await fetch('/api/translations/dynamic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          target_language: lang,
          context
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return data.translated_text;
        }
      }
    } catch (error) {
      // Dynamic translation failed
    }

    return text;
  }, [context]);

  return {
    languageCode: i18n.language,
    language: currentLanguage,
    changeLanguage,
    translateDynamic,
    supportedLanguages: context?.supportedLanguages || I18N_LANGUAGES,
    isBackendAvailable: context?.isBackendAvailable || false,
    translationStrategy: context?.translationStrategy || TRANSLATION_STRATEGY.STATIC_ONLY,
    translationsVersion: context?.translationsVersion, // 🔥 CRITICAL: Expose version for re-render triggers
    incrementTranslationsVersion: context?.incrementTranslationsVersion, // 🔥 Allow useBatchTranslation to trigger re-renders
    isProviderReady: context?.isProviderReady ?? false // 🔥 NEW: Signals when initial translations are ready
  };
};

export { 
  EnhancedI18nProvider, 
  useEnhancedLanguage, 
  TRANSLATION_STRATEGY,
  translationLoader
};
export default EnhancedI18nProvider; 